import { useState } from "react";
import { Loader2, Star, Ticket } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast } from "./BadgesSheet";
import { EndDishOffer } from "./EndDishOffer";

const COLORS = ["#D4AF37", "#221A0C", "#E25822", "#221A0C", "#D4AF37", "#221A0C", "#E25822", "#221A0C"];
const TEXT = ["#0A0A0A", "#D4AF37", "#FFFFFF", "#D4AF37", "#0A0A0A", "#D4AF37", "#FFFFFF", "#D4AF37"];

const seg = (i, total, r) => {
  const a0 = ((i * 360) / total - 90) * (Math.PI / 180);
  const a1 = (((i + 1) * 360) / total - 90) * (Math.PI / 180);
  return `M 0 0 L ${r * Math.cos(a0)} ${r * Math.sin(a0)} A ${r} ${r} 0 0 1 ${r * Math.cos(a1)} ${r * Math.sin(a1)} Z`;
};

export const SpinChakra = ({ state, refresh }) => {
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [pending, setPending] = useState(null);
  const [result, setResult] = useState(null);
  const wheel = state.wheel || [];
  const n = wheel.length || 8;
  const spins = state.spins || {};
  const canSpin = spins.free_available || spins.extra > 0;

  const spin = async () => {
    if (spinning) return;
    setResult(null);
    setSpinning(true);
    try {
      const r = await api.post("/arcade/spin", { user_id: USER_ID });
      setPending(r.data);
      const target = 360 * 6 - (r.data.segment * (360 / n) + 360 / n / 2);
      setRot((prev) => prev - (prev % 360) + target);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not spin");
      setSpinning(false);
    }
  };

  const landed = () => {
    if (!pending) return;
    setResult(pending);
    badgeToast(pending.new_badges);
    setPending(null);
    setSpinning(false);
    refresh();
  };

  return (
    <div data-testid="game-spin-chakra" className="rounded-2xl bg-[#121212] border border-[#D4AF37]/30 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">MELA DA CHAKKAR · ਮੇਲੇ ਦਾ ਚੱਕਰ</p>
      <p className="text-xs text-white/50 mt-1">One free spin a day · place an order for an extra spin. Big prizes are rare — that's the mela way.</p>
      <div className="relative w-[270px] h-[270px] mx-auto mt-5">
        <div className="absolute left-1/2 -top-1 -translate-x-1/2 z-10 w-0 h-0"
          style={{ borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "18px solid #F0C742", filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.6))" }} />
        <svg viewBox="-140 -140 280 280" className="w-full h-full chakra-wheel" data-testid="chakra-wheel"
          style={{ transform: `rotate(${rot}deg)` }} onTransitionEnd={landed}>
          <circle r="138" fill="#0C0A06" stroke="#D4AF37" strokeWidth="3" />
          {wheel.map((s, i) => <path key={s.id} d={seg(i, n, 132)} fill={COLORS[i % COLORS.length]} stroke="#0A0A0A" strokeWidth="1.5" />)}
          {wheel.map((s, i) => {
            const mid = ((i + 0.5) * 360) / n - 90;
            const x = 82 * Math.cos((mid * Math.PI) / 180);
            const y = 82 * Math.sin((mid * Math.PI) / 180);
            let ang = (mid + 90) % 360;
            if (ang < 0) ang += 360;
            if (ang > 90 && ang < 270) ang -= 180; // keep lower-half labels right side up
            const words = s.label.split(" ");
            const lines = words.length > 2 ? [words.slice(0, words.length - 1).join(" "), words[words.length - 1]] : [s.label];
            return (
              <text key={`t-${s.id}`} x={x} y={y} fill={TEXT[i % TEXT.length]} fontSize={lines.length > 1 ? "8" : "9.5"} fontWeight="900"
                textAnchor="middle" dominantBaseline="middle" transform={`rotate(${ang} ${x} ${y})`}
                style={{ letterSpacing: "0.06em" }}>
                {lines.map((ln, li) => (
                  <tspan key={li} x={x} dy={li === 0 ? (lines.length > 1 ? -5 : 0) : 10}>{ln}</tspan>
                ))}
              </text>
            );
          })}
          <circle r="26" fill="#0A0A0A" stroke="#D4AF37" strokeWidth="2.5" />
          <text x="0" y="1" fill="#D4AF37" fontSize="9" fontWeight="900" textAnchor="middle" dominantBaseline="middle">URBAN</text>
        </svg>
      </div>
      <button data-testid="chakra-spin-btn" onClick={spin} disabled={spinning || !canSpin}
        className="w-full mt-5 h-12 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest disabled:opacity-40 hover:bg-[#F0C742] transition-colors flex items-center justify-center gap-2">
        {spinning ? <Loader2 size={16} className="animate-spin" /> : <Star size={15} fill="currentColor" />}
        {spinning ? "SPINNING…" : canSpin ? (spins.free_available ? "SPIN — FREE TODAY" : `SPIN (${spins.extra} EXTRA LEFT)`) : "NO SPINS LEFT TODAY"}
      </button>
      {!canSpin && !spinning && !result && (
        <p data-testid="chakra-no-spins" className="text-[11px] text-white/45 text-center mt-2">
          Come back tomorrow — or place an order today for a bonus spin.
        </p>
      )}
      {result && (
        <div data-testid="chakra-result" className="mt-4 rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-4 text-center">
          <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">{result.kind === "again" ? "SO CLOSE!" : "YOU WON"}</p>
          <p className="font-display text-2xl font-bold mt-1">{result.label}</p>
          {result.code && (
            <p data-testid="chakra-code" className="mt-2 flex items-center justify-center gap-2 text-sm">
              <Ticket size={15} className="text-[#D4AF37]" />
              Show code <span className="font-mono font-black text-[#D4AF37] tracking-widest">{result.code}</span> at the counter · valid 48h
            </p>
          )}
          {result.points_result && (
            <p className="text-xs text-white/60 mt-1.5">{result.points_result.message}</p>
          )}
          {result.mela_result && (
            <p className="text-xs text-[#F0C742] font-bold mt-1.5 flex items-center justify-center gap-1.5">
              <img src="/mela/coin.png" alt="Regal Coins" className="w-4 h-4 object-contain" /> {result.mela_result.message}
            </p>
          )}
          {result.kind === "again" && <p className="text-xs text-white/50 mt-1.5">The chakra keeps turning — try your next spin!</p>}
          <EndDishOffer />
        </div>
      )}
    </div>
  );
};
